import { RecurringSeriesDetail, RecurringSeriesOccurrence } from '@spike/new-booking-model';
import { SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RECURRING_SERIES_OCCURRENCE_STATUS_LABELS } from 'model/RecurringSeries';
import { SERIES_PAGE_SIZE, SeriesBookingsFilter } from './useRecurringSeriesView';

type SeriesFilterOption = SelectDropdownOption<SeriesBookingsFilter>;

interface SeriesBookings {
    options: Array<SeriesFilterOption>;
    selected: SeriesFilterOption;
    rows: Array<RecurringSeriesOccurrence>;
    page: number;
    totalPages: number;
    total: number;
    firstShown: number;
    lastShown: number;
    changePage: (page: number) => void;
}

export const useRecurringSeriesBookings = (
    detail: RecurringSeriesDetail,
    filter: SeriesBookingsFilter,
    requestPage: (page: number) => void
): SeriesBookings => {
    const [localPage, setLocalPage] = useState(1);
    const recoveredEmptyPage = useRef(false);
    const pagination = detail.pagination;

    useEffect(() => {
        setLocalPage(1);
    }, [filter]);

    useEffect(() => {
        if (!pagination) {
            return;
        }

        if (detail.occurrences.length > 0) {
            recoveredEmptyPage.current = false;
            return;
        }

        if (pagination.totalRecords > 0 && pagination.page !== pagination.totalPages && !recoveredEmptyPage.current) {
            recoveredEmptyPage.current = true;
            requestPage(pagination.totalPages);
        }
    }, [detail, pagination, requestPage]);

    const counts = detail.statusCounts;

    const occurrences = useMemo(
        () =>
            pagination || filter === 'all'
                ? detail.occurrences
                : detail.occurrences.filter(occurrence => occurrence.status === filter),
        [detail.occurrences, pagination, filter]
    );

    const seriesTotal = counts
        ? Object.values(counts).reduce((sum, count) => sum + count, 0)
        : detail.occurrences.length;

    const options = useMemo<Array<SeriesFilterOption>>(() => {
        const statusCounts =
            counts ??
            detail.occurrences.reduce<Partial<Record<RecurringSeriesOccurrence['status'], number>>>(
                (totals, occurrence) => ({ ...totals, [occurrence.status]: (totals[occurrence.status] ?? 0) + 1 }),
                {}
            );

        const statusOptions = (Object.entries(statusCounts) as Array<[RecurringSeriesOccurrence['status'], number]>)
            .filter(([, count]) => count > 0)
            .map(([status, count]) => ({
                id: status,
                label: `${RECURRING_SERIES_OCCURRENCE_STATUS_LABELS[status]} (${count})`
            }));

        return [{ id: 'all', label: `All (${seriesTotal})` }, ...statusOptions];
    }, [counts, detail.occurrences, seriesTotal]);

    const total = pagination ? pagination.totalRecords : occurrences.length;
    const size = pagination ? pagination.size : SERIES_PAGE_SIZE;
    const totalPages = pagination ? pagination.totalPages : Math.max(1, Math.ceil(total / SERIES_PAGE_SIZE));
    const page = pagination ? pagination.page : Math.min(localPage, totalPages);
    const offset = (page - 1) * size;

    const rows = useMemo(
        () => (pagination ? occurrences : occurrences.slice(offset, offset + SERIES_PAGE_SIZE)),
        [occurrences, pagination, offset]
    );

    return {
        options,
        selected: options.find(option => option.id === filter) ?? options[0],
        rows,
        page,
        totalPages,
        total,
        firstShown: rows.length === 0 ? 0 : offset + 1,
        lastShown: offset + rows.length,
        changePage: pagination ? requestPage : setLocalPage
    };
};
